import { buildNavigation, essentialRoutesForRole } from './navigation-menu';
import { NotificationListComponent } from './notification-list';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import type { SupportedLang } from '../core/services/language';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { AuthService } from '../core/services/auth';
import * as Roles from '../core/permissions/role-groups';
import { NotificationService } from '../core/services/notification';
import { ClinicService } from '../core/services/clinic';
import { BrandingService } from '../core/services/branding';
import { SocketService } from '../core/services/socket';
import { ThemeService } from '../core/services/theme';
import { LanguageService } from '../core/services/language';
import { BreadcrumbComponent } from '../shared/components/breadcrumb/breadcrumb';
import { GlobalSearchComponent } from '../shared/components/global-search/global-search';
import { OfflineStatusComponent } from '../shared/components/offline-status';
import { GuidedTourOverlayComponent } from '../shared/components/guided-tour-overlay/guided-tour-overlay';
import { WorkspaceNavComponent } from '../shared/components/workspace-nav/workspace-nav';
import { NavLayoutService } from '../core/services/nav-layout';
import { findWorkspaceGroup, hasContextualNavigation } from '../shared/components/workspace-nav/workspace-navigation';
import { GuidedTourCatalogService, GuidedTourRunnerService } from '../core/services/guided-tour';
import { TranslateModule } from '@ngx-translate/core';
import { DIALOG_SM } from '../shared/ui/dialog-sizes';

import { ButtonDirective } from 'primeng/button';
import { OverlayBadge } from 'primeng/overlaybadge';
import { Popover } from 'primeng/popover';
import { Avatar } from 'primeng/avatar';
import { ConfirmDialog } from 'primeng/confirmdialog';
import { Tooltip } from 'primeng/tooltip';
import { MenuItem } from 'primeng/api';

@Component({
  selector: 'app-main-layout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    ButtonDirective,
    OverlayBadge,
    Popover,
    Avatar,
    Tooltip,
    BreadcrumbComponent,
    GlobalSearchComponent,
    OfflineStatusComponent,
    GuidedTourOverlayComponent,
    ConfirmDialog,
    WorkspaceNavComponent,
    NotificationListComponent,
    TranslateModule,
  ],
  template: `
    <div
      class="h-screen flex overflow-hidden bg-canvas"
      (click)="rememberListView($event)"
    >
      @if (!posMode()) {
        @if (sidebarOpen()) {
          <div
            class="fixed inset-0 z-30 bg-black/40 lg:hidden"
            aria-hidden="true"
            (click)="sidebarOpen.set(false)"
          ></div>
        }

        <!-- Barre latérale unique à rubriques dépliables. Sous 1024 px, elle devient un
             tiroir ouvert par le bouton menu de la barre du haut. -->
        <aside
          id="app-sidebar"
          data-tour="sidebar"
          class="flex flex-col bg-panel border-e border-hairline flex-shrink-0 z-40
                 max-lg:fixed max-lg:inset-y-0 max-lg:start-0 max-lg:w-[15rem]
                 max-lg:transition-[transform,visibility] max-lg:duration-200
                 lg:overflow-hidden lg:transition-[width] lg:duration-[260ms] lg:ease-[cubic-bezier(0.32,0.72,0,1)]"
          [class.lg:hidden]="horizontalNav()"
          [class.lg:w-60]="sidebarOpen()"
          [class.lg:w-0]="!sidebarOpen()"
          [class.lg:invisible]="!sidebarOpen()"
          [class.max-lg:-translate-x-full]="!sidebarOpen()"
          [class.rtl:max-lg:translate-x-full]="!sidebarOpen()"
          [class.max-lg:invisible]="!sidebarOpen()"
          [class.max-lg:[box-shadow:var(--shadow-floating)]]="sidebarOpen()"
          (keydown.escape)="closeDrawer()"
        >
          <div class="w-[15rem] flex flex-col flex-1 min-h-0">
            <div class="px-5 h-14 min-h-14 flex items-center justify-between gap-2 border-b border-hairline">
              <a routerLink="/dashboard" class="flex min-w-0 items-center gap-3" [attr.aria-label]="brandName()">
                <img
                  [src]="brandLogoUrl()"
                  alt=""
                  class="h-9 w-9 object-contain"
                  (error)="onLogoError($event)"
                />
                <div class="flex min-w-0 flex-col">
                  <span class="truncate text-sm font-extrabold text-ink tracking-tight">{{ brandName() }}</span>
                  <span class="text-[10px] text-ink-muted font-medium">{{ 'LAYOUT.TAGLINE' | translate }}</span>
                </div>
              </a>
              <button
                type="button"
                class="lg:hidden flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center rounded-lg text-ink-muted hover:text-ink"
                [attr.aria-label]="'LAYOUT.CLOSE_MENU' | translate"
                (click)="sidebarOpen.set(false)"
              >
                <i class="pi pi-times" aria-hidden="true"></i>
              </button>
            </div>

            <div class="px-3 py-2 border-b border-hairline">
              <div
                class="grid grid-cols-2 gap-1 rounded-xl bg-chrome p-1"
                role="group"
                [attr.aria-label]="'LAYOUT.MENU_DENSITY' | translate"
              >
                <button
                  type="button"
                  class="min-h-11 rounded-lg px-2 py-1.5 text-xs font-bold transition-colors"
                  [class.bg-panel]="menuMode() === 'essential'"
                  [class.text-primary-text]="menuMode() === 'essential'"
                  [class.shadow-sm]="menuMode() === 'essential'"
                  [class.text-ink-muted]="menuMode() !== 'essential'"
                  [attr.aria-pressed]="menuMode() === 'essential'"
                  (click)="setMenuMode('essential')"
                >
                  {{ 'LAYOUT.ESSENTIAL' | translate }}
                </button>
                <button
                  type="button"
                  class="min-h-11 rounded-lg px-2 py-1.5 text-xs font-bold transition-colors"
                  [class.bg-panel]="menuMode() === 'all'"
                  [class.text-primary-text]="menuMode() === 'all'"
                  [class.shadow-sm]="menuMode() === 'all'"
                  [class.text-ink-muted]="menuMode() !== 'all'"
                  [attr.aria-pressed]="menuMode() === 'all'"
                  (click)="setMenuMode('all')"
                >
                  {{ 'LAYOUT.ALL' | translate }}
                </button>
              </div>
            </div>

            <div class="px-4 py-2 border-b border-hairline">
              <label for="sidebar-menu-filter" class="block mb-1 text-xs font-semibold text-ink-muted">{{ 'LAYOUT.FILTER_MENU' | translate }}</label>
              <div class="relative">
                <i
                  class="pi pi-search absolute start-3 top-1/2 -translate-y-1/2 text-xs text-ink-muted"
                  aria-hidden="true"
                ></i>
                <input
                  id="sidebar-menu-filter"
                  type="text"
                  [ngModel]="sidebarFilter()"
                  (ngModelChange)="sidebarFilter.set($event)"
                  (keydown.escape)="clearFilterOnEscape($event)"
                  [placeholder]="'LAYOUT.FILTER_MENU' | translate"
                  [attr.aria-label]="'LAYOUT.FILTER_MENU_ARIA' | translate"
                  class="w-full min-h-11 ps-8 pe-12 py-2 text-sm bg-chrome border border-hairline rounded-lg
                            focus:outline-none focus:ring-2 focus:ring-primary-base focus:border-transparent"
                />
                @if (sidebarFilter()) {
                  <button
                    type="button"
                    [attr.aria-label]="'LAYOUT.CLEAR_FILTER' | translate"
                    class="absolute end-0 top-1/2 -translate-y-1/2 flex min-h-11 min-w-11 items-center justify-center rounded-lg text-ink-muted hover:text-ink text-xs"
                    (click)="sidebarFilter.set('')"
                  >
                    <i class="pi pi-times" aria-hidden="true"></i>
                  </button>
                }
              </div>
              @if (sidebarFilter() && filteredMenuItems().length === 0) {
                <p class="text-xs text-ink-muted mt-2 px-1" role="status">{{ 'LAYOUT.NO_MATCH' | translate }}</p>
              }
            </div>

            <nav class="py-3 px-3 flex-1 overflow-y-auto min-h-0" [attr.aria-label]="'LAYOUT.MENU' | translate">
              @for (section of filteredMenuItems(); track section.label) {
                <div class="mb-2">
                  <button
                    type="button"
                    (click)="toggleSection(section)"
                    class="w-full flex items-center justify-between gap-2 px-3 py-2 text-start text-[10px] font-extrabold uppercase tracking-[0.1em] text-ink-muted hover:text-ink transition-colors cursor-pointer"
                    [attr.aria-expanded]="isSectionOpen(section)"
                    [attr.aria-controls]="isSectionOpen(section) ? 'section-items-' + section.id : null"
                  >
                    <span class="flex-1 text-start leading-snug">{{ section.label }}</span>
                    <i
                      class="pi text-[10px] text-ink-subtle"
                      aria-hidden="true"
                      [class.pi-chevron-down]="isSectionOpen(section)"
                      [class.pi-chevron-right]="!isSectionOpen(section)"
                      [class.rtl:rotate-180]="!isSectionOpen(section)"
                    ></i>
                  </button>

                  @if (isSectionOpen(section) && section.items) {
                    <div [id]="'section-items-' + section.id" class="flex flex-col gap-0.5 mt-0.5 mb-2">
                      @for (item of section.items; track item.label) {
                        @if (item.groupStart) {
                          <div
                            class="flex items-center gap-2 pt-3 pb-1 px-3"
                            role="separator"
                            [attr.aria-label]="item.groupStart"
                          >
                            <span
                              class="text-[9px] font-bold uppercase tracking-[0.15em] text-ink-muted"
                              >{{ item.groupStart }}</span
                            >
                            <span class="flex-1 h-px bg-hairline"></span>
                          </div>
                        }
                        <a
                          [routerLink]="item.routerLink"
                          [queryParams]="item.queryParams ?? null"
                          routerLinkActive
                          #rla="routerLinkActive"
                          [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                          class="flex items-center gap-2.5 px-3 py-[7px] rounded-lg text-[13px] font-medium transition-all duration-150 cursor-pointer group
                                 focus-visible:-outline-offset-2"
                          [class]="
                            rla.isActive
                              ? 'bg-primary-surface text-primary-text font-semibold'
                              : 'text-ink-muted hover:bg-chrome hover:text-ink'
                          "
                          [attr.aria-current]="rla.isActive ? 'page' : null"
                        >
                          <i
                            [class]="item.icon + ' text-[14px] w-5 text-center'"
                            [class.opacity-70]="!rla.isActive"
                            [class.opacity-100]="rla.isActive"
                            aria-hidden="true"
                          ></i>
                          <span class="min-w-0 flex-1">{{ item.label }}</span>
                        </a>
                      }
                    </div>
                  }
                </div>
              }
            </nav>

            <div class="px-3 py-3 border-t border-hairline">
              <a
                routerLink="/help"
                routerLinkActive
                #helpRla="routerLinkActive"
                [routerLinkActiveOptions]="{ exact: false }"
                data-tour="help-section"
                class="group relative flex items-center gap-2.5 rounded-2xl px-3 py-2.5 text-[13px] font-semibold transition-all duration-200
                       focus-visible:-outline-offset-2"
                [class]="
                  helpRla.isActive
                    ? 'bg-primary-base text-on-primary shadow-sm'
                    : 'bg-primary-surface text-primary-text hover:bg-primary-surface'
                "
                [attr.aria-current]="helpRla.isActive ? 'page' : null"
              >
                <i class="pi pi-question-circle text-base w-5 text-center" aria-hidden="true"></i>
                <span>{{ 'GUIDED_TOUR.HELP_DOCS' | translate }}</span>
              </a>
            </div>
          </div>
        </aside>
      }

      <!-- La barre latérale occupe toute la hauteur ; l'en-tête et le contenu
           se partagent la colonne de droite. -->
      <div class="flex flex-1 flex-col min-w-0 overflow-hidden">
        @if (!posMode()) {
          <!-- La classe app-topbar sert d'ancre au thème sombre : sans elle, la règle
               visait le tag header et repeignait aussi chaque en-tête de page.
               La barre est un matériau translucide, pas un bandeau de marque : le
               contenu défile dessous et la couleur reste disponible pour ce qui
               signale quelque chose. -->
          <header
            class="app-topbar bg-panel flex items-center justify-between px-4 h-14 min-h-14 border-b border-hairline z-30 flex-shrink-0 print:hidden"
          >
            <div class="flex items-center gap-3">
              @if (horizontalNav()) {
                <!-- En menu horizontal, la barre latérale (et son logo) est masquée : le logo passe ici. -->
                <a routerLink="/dashboard" class="hidden flex-shrink-0 items-center gap-2 pe-2 lg:flex" [attr.aria-label]="brandName()">
                  <img [src]="brandLogoUrl()" alt="" class="h-8 w-8 object-contain" (error)="onLogoError($event)" />
                  <span class="text-sm font-extrabold text-ink tracking-tight">{{ brandName() }}</span>
                </a>
              }
              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                icon="pi pi-bars"
                class="!text-ink-muted hover:!bg-ink/6"
                [class.lg:!hidden]="horizontalNav()"
                [attr.aria-label]="'LAYOUT.TOGGLE_SIDEBAR' | translate"
                [attr.aria-expanded]="sidebarOpen()"
                aria-controls="app-sidebar"
                (click)="sidebarOpen.set(!sidebarOpen())"
              ></button>
              <button
                type="button"
                data-tour="global-search"
                class="hidden sm:flex h-10 w-64 md:w-80 items-center gap-2 rounded-control border border-hairline bg-panel px-3 text-start text-sm text-ink-muted transition-colors hover:border-primary-border hover:bg-canvas"
                [attr.aria-label]="'LAYOUT.SEARCH_ARIA' | translate"
                (click)="globalSearch.open()"
              >
                <i class="pi pi-search text-sm" aria-hidden="true"></i>
                <span class="min-w-0 flex-1 truncate">{{ 'LAYOUT.SEARCH_PLACEHOLDER' | translate }}</span>
                <kbd class="rounded border border-hairline bg-canvas px-1.5 py-0.5 text-[10px] font-medium">Ctrl K</kbd>
              </button>
            </div>

            <div class="flex items-center gap-1">
              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                icon="pi pi-search"
                class="sm:hidden !text-ink-muted hover:!bg-ink/6"
                [pTooltip]="'LAYOUT.SEARCH_TOOLTIP' | translate"
                [attr.aria-label]="'LAYOUT.SEARCH_ARIA' | translate"
                (click)="globalSearch.open()"
              ></button>

              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                icon="pi pi-compass"
                class="max-sm:!hidden !text-ink-muted hover:!bg-ink/6"
                [pTooltip]="'LAYOUT.START_TOUR' | translate"
                [attr.aria-label]="'LAYOUT.START_TOUR' | translate"
                (click)="startQuickTour()"
              ></button>

              @if (isSimpleEligible()) {
                <button
                  pButton
                  type="button"
                  [text]="true"
                  [rounded]="true"
                  icon="pi pi-th-large"
                  class="max-sm:!hidden !text-ink-muted hover:!bg-ink/6"
                  [pTooltip]="'LAYOUT.SWITCH_SIMPLE_LONG' | translate"
                  [attr.aria-label]="'LAYOUT.SWITCH_SIMPLE' | translate"
                  (click)="switchToSimple()"
                ></button>
              }

              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                [icon]="themeService.darkMode() ? 'pi pi-sun' : 'pi pi-moon'"
                class="max-sm:!hidden !text-ink-muted hover:!bg-ink/6"
                [pTooltip]="'LAYOUT.CHANGE_THEME' | translate"
                [attr.aria-label]="(themeService.darkMode() ? 'LAYOUT.LIGHT_MODE' : 'LAYOUT.DARK_MODE') | translate"
                [attr.aria-pressed]="themeService.darkMode()"
                (click)="themeService.toggle()"
              ></button>

              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                class="max-sm:!hidden !text-ink-muted hover:!bg-ink/6 font-semibold text-sm !px-2"
                [pTooltip]="'LAYOUT.LANGUAGE' | translate"
                [attr.aria-label]="'LAYOUT.SELECT_LANGUAGE' | translate"
                (click)="langPanel.toggle($event)"
              >
                {{ languageService.getCurrentLanguage().flag }}
                {{ languageService.currentLang() | uppercase }}
              </button>

              <!-- Ne s'affiche qu'en cas de coupure réseau ou de modifications
                   en attente : au quotidien, la barre reste inchangée. -->
              <app-offline-status />

              @if (unreadCount() > 0) {
                <p-overlaybadge [value]="unreadCount()" severity="danger">
                  <button
                    pButton
                    type="button"
                    [text]="true"
                    [rounded]="true"
                    icon="pi pi-bell"
                    [attr.aria-label]="'LAYOUT.NOTIFICATIONS_UNREAD' | translate: { count: unreadCount() }"
                    class="!text-ink-muted hover:!bg-ink/6"
                    (click)="notifPanel.toggle($event)"
                  ></button>
                </p-overlaybadge>
              } @else {
                <button
                  pButton
                  type="button"
                  [text]="true"
                  [rounded]="true"
                  icon="pi pi-bell"
                  [attr.aria-label]="'LAYOUT.NOTIFICATIONS' | translate"
                  class="!text-ink-muted hover:!bg-ink/6"
                  (click)="notifPanel.toggle($event)"
                ></button>
              }

              <div class="hidden md:flex flex-col items-end leading-tight mx-2 text-ink">
                <span class="text-sm font-semibold">{{ authService.currentUser()?.name }}</span>
                <span class="text-[10px] bg-chrome text-ink-muted px-2 py-0.5 rounded-full uppercase font-semibold">
                  {{ roleLabelKey() | translate }}
                </span>
              </div>

              <button
                type="button"
                class="rounded-full"
                aria-haspopup="true"
                [attr.aria-label]="'LAYOUT.OPEN_PROFILE' | translate"
                (click)="profilePanel.toggle($event)"
              >
                <p-avatar
                  icon="pi pi-user"
                  shape="circle"
                  styleClass="cursor-pointer !w-8 !h-8 !bg-chrome !text-ink-muted border border-hairline"
                >
                </p-avatar>
              </button>

              <button
                pButton
                type="button"
                [text]="true"
                [rounded]="true"
                icon="pi pi-sign-out"
                class="max-sm:!hidden !text-ink-muted hover:!bg-ink/6 ms-1"
                [attr.aria-label]="'LAYOUT.SIGN_OUT' | translate"
                [pTooltip]="'LAYOUT.SIGN_OUT' | translate"
                (click)="logout()"
              ></button>
            </div>
          </header>

          @if (horizontalNav()) {
            <!-- Menu horizontal (≥ 1024 px) : les espaces, puis les entrées de l'espace actif. -->
            <nav
              class="hidden flex-shrink-0 border-b border-hairline bg-panel px-4 lg:block print:hidden"
              [attr.aria-label]="'LAYOUT.MAIN_MENU' | translate"
            >
              <ul class="flex items-stretch gap-1 overflow-x-auto">
                @for (section of filteredMenuItems(); track section.label) {
                  <li>
                    <button
                      type="button"
                      class="flex min-h-11 items-center gap-2 whitespace-nowrap px-3 text-sm font-medium transition-colors border-b-2"
                      [class.font-bold]="isActiveSection(section)"
                      [class.text-primary-text]="isActiveSection(section)"
                      [class.text-ink-muted]="!isActiveSection(section)"
                      [class.hover:text-ink]="!isActiveSection(section)"
                      [class.border-primary-base]="isActiveSection(section)"
                      [class.border-transparent]="!isActiveSection(section)"
                      [attr.aria-pressed]="isActiveSection(section)"
                      (click)="selectSection(section)"
                    >
                      <i [class]="(section.icon || 'pi pi-folder') + ' text-[14px]'" aria-hidden="true"></i>
                      {{ section.label }}
                    </button>
                  </li>
                }
              </ul>
              @if (activeSection(); as active) {
                <ul class="flex items-center gap-1 overflow-x-auto border-t border-hairline py-1.5">
                  @for (item of active.items; track item.label) {
                    <li>
                      <a
                        [routerLink]="item.routerLink"
                        [queryParams]="item.queryParams ?? null"
                        routerLinkActive
                        #hrla="routerLinkActive"
                        [routerLinkActiveOptions]="{ exact: item.exact ?? false }"
                        class="flex min-h-9 items-center gap-2 whitespace-nowrap rounded-lg px-3 text-[13px] font-medium transition-colors"
                        [class.bg-primary-surface]="hrla.isActive"
                        [class.text-primary-text]="hrla.isActive"
                        [class.font-semibold]="hrla.isActive"
                        [class.text-ink-muted]="!hrla.isActive"
                        [class.hover:bg-chrome]="!hrla.isActive"
                        [class.hover:text-ink]="!hrla.isActive"
                        [attr.aria-current]="hrla.isActive ? 'page' : null"
                      >
                        <i [class]="item.icon + ' text-[14px]'" aria-hidden="true"></i>
                        {{ item.label }}
                      </a>
                    </li>
                  }
                </ul>
              }
            </nav>
          }
        }

        <main class="flex-1 overflow-auto bg-canvas">
          @if (!posMode() && !hasWorkspaceNavigation()) {
            <div class="px-6 pt-3 pb-0">
              <app-breadcrumb [menuLabels]="breadcrumbMenuLabels()" />
            </div>
          }
          <div [class]="posMode() ? 'h-full p-4' : 'px-6 pb-6 pt-2'">
            @if (!posMode()) {
              <app-workspace-nav />
            }
            <router-outlet></router-outlet>
          </div>
        </main>
      </div>

      <p-popover #langPanel>
        <div class="flex flex-col gap-1 py-1 min-w-[140px]">
          @for (lang of languageService.languages; track lang.code) {
            <button
              class="flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer text-start w-full"
              [class.bg-primary-surface]="languageService.currentLang() === lang.code"
              [class.text-primary-text]="languageService.currentLang() === lang.code"
              [class.hover:bg-chrome]="languageService.currentLang() !== lang.code"
              (click)="setLanguage(lang.code); langPanel.hide()"
            >
              <span class="text-lg">{{ lang.flag }}</span>
              <span>{{ lang.label }}</span>
              @if (languageService.currentLang() === lang.code) {
                <i class="pi pi-check ms-auto text-primary-base text-xs" aria-hidden="true"></i>
              }
            </button>
          }
        </div>
      </p-popover>

      <p-popover #profilePanel>
        <div class="flex min-w-56 flex-col gap-1 py-1">
          <div class="mb-2 border-b border-hairline px-3 pb-3">
            <p class="text-sm font-bold text-ink">{{ authService.currentUser()?.name }}</p>
            <p class="mt-0.5 text-xs uppercase text-ink-muted">
              {{ roleLabelKey() | translate }}
            </p>
          </div>
          @if (canConfigureSignature()) {
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
              (click)="navigate('/signature-config'); profilePanel.hide()"
            >
              <i class="pi pi-pen-to-square text-ink-muted" aria-hidden="true"></i>
              <span>{{ 'LAYOUT.MY_SIGNATURE' | translate }}</span>
            </button>
          }
          <!-- Sur petit écran, la barre ne garde que la loupe et les notifications. -->
          <div class="flex flex-col gap-1 sm:hidden">
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
              (click)="profilePanel.hide(); startQuickTour()"
            >
              <i class="pi pi-compass text-ink-muted" aria-hidden="true"></i>
              <span>{{ 'LAYOUT.START_TOUR' | translate }}</span>
            </button>
            @if (isSimpleEligible()) {
              <button
                type="button"
                class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
                (click)="profilePanel.hide(); switchToSimple()"
              >
                <i class="pi pi-th-large text-ink-muted" aria-hidden="true"></i>
                <span>{{ 'LAYOUT.SWITCH_SIMPLE' | translate }}</span>
              </button>
            }
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
              [attr.aria-pressed]="themeService.darkMode()"
              (click)="themeService.toggle()"
            >
              <i [class]="themeService.darkMode() ? 'pi pi-sun text-ink-muted' : 'pi pi-moon text-ink-muted'" aria-hidden="true"></i>
              <span>{{ 'LAYOUT.CHANGE_THEME' | translate }}</span>
            </button>
            @for (lang of languageService.languages; track lang.code) {
              <button
                type="button"
                class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
                [class.font-bold]="languageService.currentLang() === lang.code"
                [attr.aria-pressed]="languageService.currentLang() === lang.code"
                (click)="setLanguage(lang.code); profilePanel.hide()"
              >
                <i class="pi pi-globe text-ink-muted" aria-hidden="true"></i>
                <span>{{ lang.label }}</span>
              </button>
            }
            <button
              type="button"
              class="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-start text-sm font-medium text-ink hover:bg-chrome"
              (click)="profilePanel.hide(); logout()"
            >
              <i class="pi pi-sign-out text-ink-muted" aria-hidden="true"></i>
              <span>{{ 'LAYOUT.SIGN_OUT' | translate }}</span>
            </button>
          </div>
        </div>
      </p-popover>

      <p-popover #notifPanel styleClass="w-80 max-w-[calc(100vw-2rem)] max-h-[70vh] overflow-y-auto">
        <app-notification-list
          [notifications]="notificationService.notifications()"
          (activate)="markAsRead($event.id!); navigateToNotification($event)"
          (readAll)="markAllAsRead()"
        />
      </p-popover>

      <app-global-search #globalSearch />
      <app-guided-tour-overlay />
      <!-- Dialogue de confirmation unique : les écrans appellent ConfirmationService.confirm()
           sans embarquer leur propre <p-confirmdialog> (recette dans DESIGN.md). -->
      <p-confirmdialog [style]="confirmDialog.style" [breakpoints]="confirmDialog.breakpoints" />

    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        height: 100%;
      }
      aside ::-webkit-scrollbar {
        width: 4px;
      }
      aside ::-webkit-scrollbar-track {
        background: transparent;
      }
      aside ::-webkit-scrollbar-thumb {
        background: var(--color-hairline-strong);
        border-radius: 10px;
      }
      `,
  ],
})
export class MainLayoutComponent implements OnInit {
  authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  notificationService = inject(NotificationService);
  clinicService = inject(ClinicService);
  private brandingService = inject(BrandingService);
  protected get confirmDialog() { return DIALOG_SM; }
  readonly brandName = computed(() => this.brandingService.branding().displayName);
  readonly brandLogoUrl = computed(() => this.brandingService.branding().logoUrl || 'logo-icakene.png');
  themeService = inject(ThemeService);
  languageService = inject(LanguageService);
  private socketService = inject(SocketService);
  private router = inject(Router);
  private readonly tourCatalog = inject(GuidedTourCatalogService);
  private readonly tourRunner = inject(GuidedTourRunnerService);


  constructor() {
    effect(() => {
      this.clinicService.settings(); // track settings signal for reactive rebuild
      // Le menu est assemblé en TypeScript, pas dans le gabarit : il doit donc
      // être reconstruit quand le dictionnaire arrive et à chaque changement
      // de langue, sinon il resterait figé sur la langue du premier rendu.
      this.languageService.revision();
      this.buildMenu();
    });
    effect(() => {
      const selector = this.tourRunner.step()?.selector ?? '';
      untracked(() => this.syncDrawerWithTour(selector));
    });
  }

  /**
   * Libellé traduit pour les entrées de menu.
   *
   * Passe par le service plutôt que par le pipe parce que `item.label` sert
   * aussi à la recherche et aux attributs aria : la valeur stockée
   * doit être un texte affichable, pas une clé.
   */
  private t(key: string): string {
    return this.languageService.instant(key);
  }

  private readonly navLayout = inject(NavLayoutService);
  readonly horizontalNav = computed(() => this.navLayout.layout() === 'horizontal');
  readonly sidebarOpen = signal(typeof window === 'undefined' ? true : window.innerWidth >= 1024);
  readonly menuItemsSig = signal<any[]>([]);
  get menuItems(): MenuItem[] {
    return this.menuItemsSig() as MenuItem[];
  }
  set menuItems(value: MenuItem[]) {
    this.menuItemsSig.set(value as any[]);
  }

  readonly sidebarFilter = signal('');
  private drawerOpenedByTour = false;
  readonly roleLabelKey = computed(
    () => `ROLES.${(this.authService.currentUser()?.role ?? '').toUpperCase()}`,
  );
  readonly currentPath = signal(this.router.url);
  readonly posMode = computed(() => this.currentPath().split('?')[0] === '/pharmacy-pos');
  readonly hasWorkspaceNavigation = computed(() => hasContextualNavigation(this.currentPath()));
  /** Le fil d'Ariane reprend les libellés traduits du menu pour les pages qu'il connaît. */
  readonly breadcrumbMenuLabels = computed(() => {
    const labels: Record<string, string> = {};
    for (const section of this.menuItemsSig()) {
      for (const item of section.items ?? []) labels[item.routerLink] = item.label;
    }
    return labels;
  });

  private readonly MENU_MODE_KEY = 'icakene_menu_mode';
  readonly menuMode = signal<'essential' | 'all'>(
    typeof localStorage === 'undefined'
      ? 'essential'
      : localStorage.getItem(this.MENU_MODE_KEY) === 'all'
        ? 'all'
        : 'essential',
  );

  /**
   * Une entrée par espace métier. Les vues secondaires restent disponibles
   * via les onglets d'espace et la recherche menu.
   */

  readonly filteredMenuItems = computed(() => {
    const raw = this.sidebarFilter().trim();
    const currentRoute = this.currentPath().split('?')[0].split('#')[0];
    const showAll = this.menuMode() === 'all' || !!raw;
    const all = showAll
      ? this.menuItemsSig()
      : this.menuItemsSig()
          .map((section: any) => ({
            ...section,
            items: (section.items ?? []).filter(
              (item: any) =>
                essentialRoutesForRole(this.authService.currentUser()?.role).has(item.routerLink) ||
                this.itemMatchesRoute(item, currentRoute),
            ),
          }))
          .filter((section: any) => section.items.length > 0);
    if (!raw) return all;
    const norm = (s: string) =>
      (s || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
    const q = norm(raw);
    const result: any[] = [];
    for (const section of all) {
      const matches = (section.items ?? []).filter((it: any) => norm(it.label).includes(q));
      if (matches.length) {
        result.push({ ...section, items: matches, expanded: true });
      } else if (norm(section.label).includes(q)) {
        result.push({ ...section, expanded: true });
      }
    }
    return result;
  });

  /** Espace choisi à la main dans le rail ; la navigation le remet à zéro pour suivre la route. */
  private readonly manualSection = signal<string | null>(null);

  /** Espace affiché dans le panneau : le choix du rail, sinon celui qui contient la page courante. */
  readonly activeSection = computed<any | null>(() => {
    const sections = this.filteredMenuItems();
    const manual = this.manualSection();
    return (
      sections.find((section: any) => section.label === manual) ??
      sections.find((section: any) => this.sectionHasActiveRoute(section)) ??
      sections[0] ??
      null
    );
  });

  /** Pendant une recherche, le panneau liste les résultats de tous les espaces. */
  readonly panelSections = computed<any[]>(() => {
    if (this.sidebarFilter().trim()) return this.filteredMenuItems();
    const active = this.activeSection();
    return active ? [active] : [];
  });

  isActiveSection(section: any): boolean {
    return !this.sidebarFilter().trim() && this.activeSection()?.label === section.label;
  }

  selectSection(section: any): void {
    const alreadyActive = this.isActiveSection(section);
    this.sidebarFilter.set('');
    this.manualSection.set(alreadyActive ? null : section.label);
  }

  private readonly SIDEBAR_EXPANDED_KEY = 'icakene_sidebar_expanded';
  /** Rubriques dépliées ou repliées à la main, par identifiant d'espace (mémorisé). */
  private readonly sidebarExpandedState = signal<Record<string, boolean>>(this.loadSidebarExpandedState());

  private loadSidebarExpandedState(): Record<string, boolean> {
    try {
      const raw = localStorage.getItem(this.SIDEBAR_EXPANDED_KEY);
      return raw ? (JSON.parse(raw) as Record<string, boolean>) : {};
    } catch {
      return {};
    }
  }

  private sectionKey(section: any): string {
    return section.id ?? section.label;
  }

  /** Une rubrique est ouverte selon le choix mémorisé, sinon si elle contient la page ; toujours pendant un filtrage. */
  isSectionOpen(section: any): boolean {
    if (this.sidebarFilter().trim()) return true;
    return this.sidebarExpandedState()[this.sectionKey(section)] ?? this.sectionHasActiveRoute(section);
  }

  toggleSection(section: any): void {
    const key = this.sectionKey(section);
    const open = !this.isSectionOpen(section);
    this.setSectionExpanded(key, open);
  }

  private setSectionExpanded(key: string, open: boolean): void {
    this.sidebarExpandedState.update((state) => ({ ...state, [key]: open }));
    try {
      localStorage.setItem(this.SIDEBAR_EXPANDED_KEY, JSON.stringify(this.sidebarExpandedState()));
    } catch {
      // le choix reste valable pour la session
    }
  }

  /** Après une navigation, la rubrique de la page courante se déplie, même si on l'avait repliée. */
  private autoExpandActiveSection(): void {
    for (const section of this.menuItemsSig()) {
      if (this.sectionHasActiveRoute(section) && this.sidebarExpandedState()[this.sectionKey(section)] === false) {
        this.setSectionExpanded(this.sectionKey(section), true);
      }
    }
  }

  /** Échap vide le filtre avant de refermer le tiroir. */
  clearFilterOnEscape(event: Event): void {
    if (!this.sidebarFilter()) return;
    this.sidebarFilter.set('');
    event.stopPropagation();
  }

  closeDrawer(): void {
    if (this.isNarrow() && !this.drawerOpenedByTour) this.sidebarOpen.set(false);
  }

  private isNarrow(): boolean {
    return typeof window !== 'undefined' && window.innerWidth < 1024;
  }

  /**
   * Sous 1024 px, le menu est un tiroir fermé, donc hors écran : la visite guidée
   * éclairerait une zone vide. On l'ouvre le temps des étapes qui le désignent.
   */
  private syncDrawerWithTour(selector: string): void {
    if (!this.isNarrow()) return;
    if (/data-tour="(sidebar|help-section)"/.test(selector)) {
      if (this.sidebarOpen()) return;
      this.drawerOpenedByTour = true;
      this.sidebarOpen.set(true);
      // Le tiroir glisse en 200 ms : la cible se mesure une fois arrivé.
      setTimeout(() => this.tourRunner.refreshTarget(), 260);
    } else if (this.drawerOpenedByTour) {
      this.drawerOpenedByTour = false;
      this.sidebarOpen.set(false);
    }
  }

  setMenuMode(mode: 'essential' | 'all'): void {
    this.menuMode.set(mode);
    try {
      localStorage.setItem(this.MENU_MODE_KEY, mode);
    } catch {
      // Le choix reste valide pour la session si le stockage est indisponible.
    }
  }

  readonly unreadCount = signal(0);

  ngOnInit() {
    // Couleurs, logo et nom de l'établissement de l'utilisateur connecté.
    void this.brandingService.loadMine();
    this.autoExpandActiveSection();
    this.updateUnreadCount();
    this.restoreListView();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.currentPath.set(this.router.url);
        this.manualSection.set(null);
        this.autoExpandActiveSection();
        this.closeDrawer();
        this.restoreListView();
      });
  }

  /** Mémorise aussi les bascules des pages historiques qui n'utilisent pas encore le signal partagé. */
  rememberListView(event: Event): void {
    const target = event.target instanceof Element ? event.target.closest('button') : null;
    if (!target) return;
    const label =
      `${target.getAttribute('aria-label') ?? ''} ${target.textContent ?? ''}`.toLowerCase();
    const view = label.includes('calendrier')
      ? 'calendar'
      : label.includes('tableau') || label.includes('liste')
        ? 'table'
        : label.includes('carte')
          ? 'card'
          : null;
    if (!view || (!label.includes('vue') && !/^\s*(tableau|cartes?|calendrier)\s*$/.test(label)))
      return;
    try {
      localStorage.setItem(this.listViewStorageKey(), view);
    } catch {
      /* préférence de session uniquement */
    }
  }

  private restoreListView(): void {
    let preferred: string | null = null;
    try {
      preferred = localStorage.getItem(this.listViewStorageKey());
    } catch {
      return;
    }
    if (!preferred || typeof document === 'undefined') return;
    setTimeout(() => {
      const candidates = Array.from(document.querySelectorAll<HTMLButtonElement>('main button'));
      const target = candidates.find((button) => {
        const label =
          `${button.getAttribute('aria-label') ?? ''} ${button.textContent ?? ''}`.toLowerCase();
        if (preferred === 'calendar') return label.includes('calendrier');
        if (preferred === 'table')
          return (
            label.includes('tableau') || label.trim() === 'tableau' || label.includes('vue liste')
          );
        return label.includes('carte');
      });
      target?.click();
    });
  }

  private listViewStorageKey(): string {
    return `icakene_view_route:${this.router.url.split('#')[0]}`;
  }

  sectionHasActiveRoute(section: any): boolean {
    const cleanUrl = this.currentPath().split('?')[0].split('#')[0];
    return (section.items ?? []).some((item: any) => this.itemMatchesRoute(item, cleanUrl));
  }

  /**
   * L'entrée de menu couvre-t-elle la page ? Oui pour sa route et ses sous-routes, et
   * aussi pour une page secondaire (ex. /beds) sans entrée propre : elle est rattachée
   * à l'entrée d'une des routes de son groupe d'onglets. Sert au panneau actif comme
   * au mode « Essentiel », qui sinon masquait l'entrée et laissait le panneau sur Accueil.
   */
  private itemMatchesRoute(item: any, cleanUrl: string): boolean {
    if (cleanUrl === item.routerLink || cleanUrl.startsWith(item.routerLink + '/')) return true;
    return !!findWorkspaceGroup(cleanUrl)?.tabs.some((tab) => tab.route === item.routerLink);
  }

  private hasAnyRole(roles: string[]): boolean {
    const userRole = this.authService.currentUser()?.role;
    if (userRole === 'super_admin') return true;
    return roles.includes(userRole || '');
  }

  private itemAllowed(item: any): boolean {
    if (!item.itemRoles) return true;
    return this.hasAnyRole(item.itemRoles);
  }

  private buildMenu() {
    this.menuItems = buildNavigation({
      role: this.authService.currentUser()?.role,
      t: key => this.t(key), hasAnyRole: roles => this.hasAnyRole(roles),
      itemAllowed: item => this.itemAllowed(item),
      enabledModules: this.clinicService.settings().features?.['enabledModules'] as string[] | undefined,
    });
  }

  onLogoError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const safeName = (this.brandName() || '').replace(/[<>&"']/g, '');
    img.insertAdjacentHTML(
      'afterend',
      `<span style="color:var(--color-ink);font-weight:900;font-size:1rem;letter-spacing:0.1em">${safeName}</span>`,
    );
  }

  setLanguage(code: string) {
    this.languageService.setLanguage(code as SupportedLang);
  }

  canConfigureSignature(): boolean {
    return Roles.DOCTOR_ONLY.includes(this.authService.currentUser()?.role as any);
  }

  logout() {
    this.authService.logout();
    this.router.navigate(['/auth/login']);
  }

  isSimpleEligible(): boolean {
    const role = this.authService.currentUser()?.role;
    return ['doctor', 'aide_soignante', 'receptionist', 'laborantin', 'pharmacienne'].includes(
      role || '',
    );
  }

  switchToSimple() {
    localStorage.setItem('viewMode', 'simple');
    this.router.navigateByUrl('/simple');
  }

  async startQuickTour(): Promise<void> {
    const scenarios = await this.tourCatalog.load(false);
    if (scenarios.length) await this.tourRunner.start(scenarios[0]);
  }

  clearNotifications() {
    this.notificationService.clearAll();
  }

  markAllAsRead() {
    this.notificationService.markAllAsRead();
  }

  private updateUnreadCount() {
    this.unreadCount.set(this.notificationService.notifications().filter((n) => !n.read).length);
  }

  markAsRead(id: string) {
    this.notificationService.markAsRead(id);
    this.updateUnreadCount();
  }

  navigateToNotification(notification: any) {
    if (notification.route) {
      this.router.navigate([notification.route]);
    }
  }

  stopPropagation(event: Event) {
    event.stopPropagation();
  }

  navigate(route: string): void {
    if (route) {
      this.router.navigateByUrl(route);
    }
  }

  simplifiedMenuItems() {
    return this.menuItems;
  }
}
